import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_DISCOVERED_DEVICES_GRID_WEB_COMPONENT } from '../../../../../../../../../../webcomponents.constants';
import {
  Column,
  ColumnKeys,
  FILTERS,
  filtersUiConfigs,
  getGridColumnDetails,
  GRID_COLUMNS,
  PREDEFINED_FILTERS_KEYS,
} from '../../grid-config';
import { DiscoveredDevicesGridDataProvider } from '../../DiscoveredDevicesGridDataProvider';
import { ColumnDetails } from '../../../../../../../../../../grid/types/common';
import {
  GridColumnRenderers,
  SelectionStoreMode,
  SELECTION_STORE_CONFIGURATION,
} from '../../../../../../../../../../grid/gridView/types';
import { Filter } from '../../../../../../../../../../grid/gridFilters/types';
import { SelectOption } from '../../../../../../../../../../ui/gzSelect/types';
import { PAGE_SIZE_OPTIONS } from '../../../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView/configs/page-config';
import { GridViewWebComponent } from '../../../../../../../../../../grid/gridView';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { html, PropertyValues, TemplateResult } from 'lit';
import { styles } from '../view/styles';
import { FormFieldInput } from '../../../../../../../../../../ui/gzForm/formField/component/FormFieldInput';
import { DiscoveredDeviceItem } from '@nirvana/shared/dtos/types/policies/common';
import { getTimezone } from '../../../../../../../../../../utils/global.utils';
import { ListEvents } from '../../../../../../../../../../ui/gzSingleSelectList/types';
import { GzImageRepoWebComponent } from '../../../../../../../../../../ui/gzImageRepo';
import { ImageState } from '../../../../../../../../../../ui/gzImageRepo/types';
import { formatDate } from '@nirvana/shared/time';
import { GridFilterUiConfiguration } from '../../../../../../../../../../types/filters/types';

const VIEW_GRID_ID = 'policy-discovered-devices-grid';

@customElement(WEB_COMPONENT_DISCOVERED_DEVICES_GRID_WEB_COMPONENT)
export class DiscoveredDevicesGridWebComponent extends FormFieldInput<DiscoveredDeviceItem[]> {
  protected selectedItemsGridData = [];
  protected currentSelectedItems: DiscoveredDeviceItem[] = [];

  protected allColumnDetails: ColumnDetails<Column, ColumnKeys>[] = getGridColumnDetails();
  protected columns: Column[] = GRID_COLUMNS;
  protected hasSelectableRows = true;
  protected columnRenderers: GridColumnRenderers;

  // filters settings
  protected hasFilters = true;
  protected filters: Filter<ColumnKeys>[] = FILTERS;
  protected predefinedFiltersKeys: ColumnKeys[] = PREDEFINED_FILTERS_KEYS;
  protected filterUiConfig: Array<GridFilterUiConfiguration> = filtersUiConfigs();

  // page size settings
  protected pageSizeOptions: SelectOption[] = PAGE_SIZE_OPTIONS;
  protected pageSize = 10;

  @query('#read-only-grid')
  protected gridComponent: GridViewWebComponent<Column, ColumnKeys>;

  getFormValue(): DiscoveredDeviceItem[] {
    return this.selectedItemsGridData;
  }

  setFormValue(value: DiscoveredDeviceItem[]): void {
    this.currentSelectedItems = value;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.buildGrid();
  }

  static get styles() {
    return styles;
  }
  private buildGrid(): void {
    this.gridComponent.viewId = VIEW_GRID_ID;

    // column renderers
    this.gridComponent.allColumnDetails = this.allColumnDetails;
    this.gridComponent.columnRenderers = {
      [ColumnKeys.COMPUTER_NAME]: this.computerNameRenderer.bind(this),
      [ColumnKeys.LAST_CONNECTED]: this.lastConnectedRenderer.bind(this),
    };
    this.gridComponent.columns = this.columns;
    this.gridComponent.isCompactViewChecked = false;
    this.gridComponent.hideHeaderActions = true;

    // filters settings
    if (this.hasFilters) {
      this.gridComponent.filters = this.filters;
      this.gridComponent.removeFilters = false;
      this.gridComponent.predefinedFiltersKeys = this.predefinedFiltersKeys;
      this.gridComponent.filtersUiConfigs = this.filterUiConfig;
    }

    // pagination settings
    this.gridComponent.hasPagination = true;
    this.gridComponent.pageSizeOptions = this.pageSizeOptions;
    this.gridComponent.pageSize = this.pageSize;

    this.gridComponent.dataProvider = this.getDataProvider();

    // selection settings
    this.gridComponent.hasSelectableRows = this.hasSelectableRows;
    if (this.hasSelectableRows) {
      this.gridComponent.selectionStoreConfig = {
        functionalMode: SelectionStoreMode.RECORDS,
        maxNumOfItems: SELECTION_STORE_CONFIGURATION.maxNumOfItems,
      };
    }

    if (!this.gridComponent.started) {
      this.gridComponent.injectDependencies({
        translateFn: translateFn,
        getTimezone: getTimezone,
        getUserLanguage: Application.Main.getLanguage,
      });
      this.gridComponent
        .start()
        .catch(console.error)
        .then(() => {
          const gzImageRepo = this.gridComponent.shadowRoot?.querySelector(
            '.no-results gz-image-repo'
          ) as GzImageRepoWebComponent;
          gzImageRepo.state = ImageState.NO_IMAGE;
          gzImageRepo.textContent = '';
          const gridToolbar = this.gridComponent.shadowRoot?.querySelector('.grid-header__toolbar') as HTMLElement;
          gridToolbar.style.paddingTop = '0px';
        });
    }
  }
  render() {
    return html` <div>${this.renderGrid()}</div>`;
  }
  protected renderGrid() {
    return html`
      <gz-grid-view id="read-only-grid" class="grid-view" @selectRows="${this.onSelectRows.bind(this)}">
        <div slot="no-results--additional-data">${this.renderEmptyStateGrid()}</div>
      </gz-grid-view>
    `;
  }
  protected onSelectRows(event: CustomEvent) {
    this.selectedItemsGridData = event.detail.selectionStoreData;
    this.onValuesChanged();
  }

  public onValuesChanged() {
    this.dispatchEvent(new CustomEvent(ListEvents.SELECTION_CHANGE));
  }

  protected getDataProvider(): DiscoveredDevicesGridDataProvider {
    return new DiscoveredDevicesGridDataProvider();
  }

  private renderEmptyStateGrid() {
    return html` <gz-text fontSize="14" style="padding-bottom: 150px;">${translateFn('No devices')}</gz-text> `;
  }

  private lastConnectedRenderer(
    rowData: unknown & { item: DiscoveredDeviceItem },
    columnDetails: ColumnDetails<Column, ColumnKeys>
  ): TemplateResult {
    return html`<div>${formatDate(rowData.item.lastDetectionDate)}</div>`;
  }
  private computerNameRenderer(
    rowData: unknown & { item: DiscoveredDeviceItem },
    columnDetails: ColumnDetails<Column, ColumnKeys>
  ): TemplateResult {
    return html`<div>${rowData.item.endpointIds[0]}</div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_DISCOVERED_DEVICES_GRID_WEB_COMPONENT: DiscoveredDevicesGridWebComponent;
  }
}
